import type { ComponentProps, ReactNode } from "react";

import {
  Box,
  Button,
  Callout,
  Dialog,
  Flex,
  Separator,
  Spinner,
  Text,
} from "@radix-ui/themes";

export interface OrderDetailDialogPresentationProps {
  open: boolean;
  header: ReactNode;
  summary?: ReactNode;
  hasDetail: boolean;
  contentLabel?: string;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  isNotFound: boolean;
  errorMessage?: string;
  onOpenChange: (open: boolean) => void;
  onCloseAutoFocus?: ComponentProps<typeof Dialog.Content>["onCloseAutoFocus"];
  onRetry: () => void;
}

interface DialogBodyProps {
  summary?: ReactNode;
  hasDetail: boolean;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  isNotFound: boolean;
  errorMessage?: string;
  onRetry: () => void;
}

const DialogBody = ({
  summary,
  hasDetail,
  isLoading,
  isFetching,
  isError,
  isNotFound,
  errorMessage,
  onRetry,
}: DialogBodyProps) => {
  if (isLoading) {
    return (
      <Flex align="center" justify="center" gap="2" py="6" role="status">
        <Spinner />
        <Text size="2" color="gray">
          Loading order details…
        </Text>
      </Flex>
    );
  }

  if (isNotFound) {
    return (
      <Callout.Root color="orange" role="status">
        <Callout.Text>
          This order could not be found. It may no longer be available for the
          selected account.
        </Callout.Text>
        <Button
          type="button"
          size="1"
          variant="soft"
          color="orange"
          onClick={onRetry}
          loading={isFetching}
        >
          Retry
        </Button>
      </Callout.Root>
    );
  }

  if (isError) {
    return (
      <Callout.Root color="red" role="alert">
        <Callout.Text>
          {errorMessage ?? "Order details could not be loaded."}
        </Callout.Text>
        <Button
          type="button"
          size="1"
          variant="soft"
          color="red"
          onClick={onRetry}
          loading={isFetching}
        >
          Retry
        </Button>
      </Callout.Root>
    );
  }

  return hasDetail && summary ? (
    summary
  ) : (
    <Callout.Root color="gray" role="status">
      <Callout.Text>No detail was returned for this order.</Callout.Text>
    </Callout.Root>
  );
};

export const OrderDetailDialogPresentation = ({
  open,
  header,
  summary,
  hasDetail,
  contentLabel,
  isLoading,
  isFetching,
  isError,
  isNotFound,
  errorMessage,
  onOpenChange,
  onCloseAutoFocus,
  onRetry,
}: OrderDetailDialogPresentationProps) => (
  <Dialog.Root open={open} onOpenChange={onOpenChange}>
    <Dialog.Content
      maxWidth="760px"
      maxHeight="85vh"
      data-testid="order-detail-dialog"
      onCloseAutoFocus={onCloseAutoFocus}
      className="overflow-y-auto"
    >
      <Box flexShrink="0">{header}</Box>

      <Box
        data-testid="order-detail-body"
        my="4"
        role={hasDetail ? "region" : undefined}
        aria-label={hasDetail ? contentLabel : undefined}
        tabIndex={hasDetail ? 0 : undefined}
      >
        <DialogBody
          summary={summary}
          hasDetail={hasDetail}
          isLoading={isLoading}
          isFetching={isFetching}
          isError={isError}
          isNotFound={isNotFound}
          errorMessage={errorMessage}
          onRetry={onRetry}
        />
      </Box>

      <Box data-testid="order-detail-footer" flexShrink="0">
        <Separator size="4" mb="3" />
        <Flex justify="end">
          <Dialog.Close>
            <Button type="button" variant="soft" color="gray">
              Close
            </Button>
          </Dialog.Close>
        </Flex>
      </Box>
    </Dialog.Content>
  </Dialog.Root>
);
